import { WarningCircle } from '@phosphor-icons/react';
import React from 'react';

// Central modal shown when the user tries to save a profile combination
// that violates the km × training-days matrix (see validateKmDaysMatrix
// in utils/goalValidator.js). Per coach spec 2026-06-01:
//
//   Validation errors that BLOCK the save → central modal, not toast.
//   The disabled-button-with-text approach is too easy to miss; a modal
//   forces the user to acknowledge.
//
// Single action — hard block (coach spec 2026-06-06):
//   • "תקן את הנתונים" — closes the modal, user must fix km or days.
//   The override path was removed. The km × days matrix is a hard
//   physiological floor; letting users save anyway produced single-run
//   loads that would have injured them.
//
// Props:
//   open          — bool
//   weeklyKm      — number, what the user entered
//   trainingDays  — number, derived from preferred_days chip count
//   requiredDays  — number, the matrix's minimum for that volume
//   onFix         — () → void  (close, no save)
export default function LoadBalanceModal({
  open,
  weeklyKm,
  trainingDays,
  requiredDays,
  onFix,
}) {
  if (!open) return null;
  const km = Number(weeklyKm) || 0;
  const days = Number(trainingDays) || 0;
  const avgPerDay = days > 0 ? (km / days).toFixed(1) : '—';
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-2xl bg-bg border border-border overflow-hidden">
        {/* Warning icon */}
        <div className="flex justify-center pt-6">
          <div className="w-14 h-14 rounded-2xl bg-pressed border-2 border-border flex items-center justify-center">
            <WarningCircle size={26} weight="bold" className="text-text-body" aria-hidden="true" />
          </div>
        </div>

        {/* Title + subtitle */}
        <div className="px-6 pt-4 pb-2 text-center">
          <h2 className="text-text text-lg font-bold">שים לב — עומס לא מאוזן</h2>
          <p className="text-text-muted text-sm mt-1">הנתונים שהזנת אינם תואמים זה לזה</p>
        </div>

        {/* Snapshot box — orange, with the problematic value highlighted */}
        <div className="px-6 py-4">
          <div className="rounded-xl bg-pressed border border-border p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-text-muted text-sm">ק"מ שבועי</span>
              <span className="text-text font-bold">{km} ק"מ</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-muted text-sm">ימי אימון</span>
              <span className="text-text-body font-bold">{days} {days === 1 ? 'יום' : 'ימים'}</span>
            </div>
            <div className="flex justify-between items-center border-t border-border pt-2">
              <span className="text-text-muted text-sm">דורש לפחות</span>
              <span className="text-text-body font-bold">
                {requiredDays} ימי אימון
              </span>
            </div>
            <p className="text-text-body text-xs leading-relaxed pt-1">
              ממוצע של {avgPerDay} ק"מ ליום על {days} {days === 1 ? 'יום' : 'ימים'} בלבד יוצר ריצות ארוכות מדי לרץ חובב ומעלה משמעותית את הסיכון לפציעות עומס (סטרס פרקצ׳ר, ITBS).
            </p>
          </div>
        </div>

        {/* Actions — hard block, no override */}
        <div className="px-6 pb-6">
          <button
            onClick={onFix}
            className="w-full py-3 rounded-xl btn-primary"
          >
            תקן את הנתונים
          </button>
        </div>
      </div>
    </div>
  );
}
